/**
 * Shared size ladder for MuiButton and MuiIconButton so same `size` → same height.
 *
 * Heights: small=30, medium=36, large=42.
 * Radii: small=md(6), medium=lg(8), large=xl(12) via theme.shape.radius.
 * All variants use a 1px border (transparent when not outlined) so one padding set keeps outer height equal.
 */
import type { Theme } from '@mui/material/styles';

export type ButtonSize = 'small' | 'medium' | 'large';

/** Radius keys on theme.shape.radius used by the button size ladder. */
export type ButtonRadiusKey = 'md' | 'lg' | 'xl';

export type ButtonSizeToken = {
  height: number;
  iconFontSize: number;
  /** theme.shape.radius key — small=md, medium=lg, large=xl. */
  borderRadius: ButtonRadiusKey;
  /** Horizontal padding spacing multiplier for contained / tonal / add / outlined. */
  pxContained: number;
  /** Horizontal padding spacing multiplier for text. */
  pxText: number;
};

/** Line-box height implied by Button typography (paragraphMedium / bodyMedium). */
const BUTTON_LINE_CONTENT: Record<ButtonSize, number> = {
  small: 20,
  medium: 20,
  large: 24,
};

export const BUTTON_SIZE_TOKENS: Record<ButtonSize, ButtonSizeToken> = {
  small: { height: 30, iconFontSize: 18, borderRadius: 'md', pxContained: 1.25, pxText: 0.625 },
  medium: { height: 36, iconFontSize: 20, borderRadius: 'lg', pxContained: 2, pxText: 1 },
  large: { height: 42, iconFontSize: 22, borderRadius: 'xl', pxContained: 3, pxText: 1.375 },
};

/** Resolve size ladder radius from theme.shape.radius (px number for styleOverrides). */
export function buttonBorderRadius(theme: Theme, size: ButtonSize): number {
  return theme.shape.radius[BUTTON_SIZE_TOKENS[size].borderRadius];
}

export function resolveButtonSize(size: ButtonSize | undefined): ButtonSize {
  return size || 'medium';
}

/** Prefer theme.spacing when px lands on the 4px grid; otherwise raw px. */
function formatPadY(theme: Theme, px: number): string {
  if (px % 4 === 0) {
    return theme.spacing(px / 8);
  }
  return `${px}px`;
}

function buttonPaddingY(size: ButtonSize): number {
  const sizeSpec = BUTTON_SIZE_TOKENS[size];
  return (sizeSpec.height - BUTTON_LINE_CONTENT[size] - 2) / 2;
}

function iconButtonPaddingY(size: ButtonSize): number {
  const sizeSpec = BUTTON_SIZE_TOKENS[size];
  return (sizeSpec.height - sizeSpec.iconFontSize - 2) / 2;
}

/**
 * Shared Button padding for a size × variant.
 * outlined | contained | tonal | add → contained horizontal; text → text horizontal.
 */
export function buttonPadding(theme: Theme, size: ButtonSize, variant: string | undefined): string {
  const sizeSpec = BUTTON_SIZE_TOKENS[size];
  const padY = formatPadY(theme, buttonPaddingY(size));
  const padX = variant === 'text' ? theme.spacing(sizeSpec.pxText) : theme.spacing(sizeSpec.pxContained);
  return `${padY} ${padX}`;
}

/** Shared IconButton padding for every variant (including outlined and tonal). */
export function iconButtonPadding(theme: Theme, size: ButtonSize): string {
  return formatPadY(theme, iconButtonPaddingY(size));
}
